import { useMemo, useState } from "react";
import { Card, Empty, Segmented, Tag, Timeline, Typography } from "antd";
import {
    AuditOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DollarOutlined,
    FileAddOutlined,
    FileSearchOutlined,
    FlagOutlined,
    TeamOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { displayNumbers } from "../../../../../variables/numbersFunctions";

const { Text } = Typography;

const fmtDateTime = (date) =>
    date
        ? new Intl.DateTimeFormat("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date)
        : "";

const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const montantFcfa = (value) =>
    value || value === 0 ? `${displayNumbers(Math.trunc(value))} FCFA` : null;

// Libellés / couleurs des états de décaissement (alignés sur ReglementsTab)
const ETAT_DECAISSEMENT = {
    EN_ATTENTE: { label: "En attente", color: "warning", dot: "#f59e0b" },
    VALIDE: { label: "Validé", color: "success", dot: "#16a34a" },
    EN_ATTENTE_D_ENCAISSEMENT: { label: "En attente d'encaissement", color: "processing", dot: "#2563eb" },
    ENCAISSE: { label: "Encaissé", color: "success", dot: "#16a34a" },
    CHEQUE_ANNULE: { label: "Chèque annulé", color: "error", dot: "#dc2626" },
    REJETE: { label: "Rejeté", color: "error", dot: "#dc2626" },
};

const ETAT_EVALUATION = {
    EN_ATTENTE: { label: "En attente", color: "warning", dot: "#f59e0b" },
    VALIDE: { label: "Validée", color: "success", dot: "#16a34a" },
    REJETE: { label: "Rejetée", color: "error", dot: "#dc2626" },
};

const STATUT_DOSSIER = {
    I: { label: "Mis en instance", dot: "#f59e0b" },
    O: { label: "Dossier ouvert", dot: "#2563eb" },
    T: { label: "Dossier clôturé", dot: "#16a34a" },
    S: { label: "Classé sans suite", dot: "#6b7280" },
    R: { label: "Dossier réouvert", dot: "#2563eb" },
    F: { label: "Dossier forclos", dot: "#dc2626" },
};

const beneficiaireName = (r) => {
    if (r?.beneficiaire && (r.beneficiaire.nom || r.beneficiaire.prenom)) {
        return `${r.beneficiaire.nom || ""} ${r.beneficiaire.prenom || ""}`.trim();
    }
    if (r?.beneficiaireMoral) {
        return r.beneficiaireMoral.raisonSociale || r.beneficiaireMoral.nom || "Établissement";
    }
    if (r?.beneficiaireConstat) {
        return r.beneficiaireConstat.nom || "Entité de constat";
    }
    return "Bénéficiaire";
};

const FILTERS = [
    { label: "Tout", value: "all" },
    { label: "Évaluations", value: "evaluation" },
    { label: "Règlements", value: "reglement" },
    { label: "Expertises", value: "expertise" },
];

/**
 * Onglet « Historique » — timeline chronologique de toutes les actions du dossier
 * sinistre depuis la déclaration : survenance, déclaration, évaluations,
 * règlements (y compris ceux rejetés, conservés ici en guise d'historique),
 * désignation d'experts, rapports d'expertise et statut courant du dossier.
 *
 * Les évènements sont agrégés côté client à partir des sous-objets déjà chargés
 * (aucun appel réseau supplémentaire).
 */
const HistoriqueTab = ({ sinistre, evaluations, decaissements, experts, expertises }) => {
    const [filter, setFilter] = useState("all");

    const numero = sinistre?.numSinistre || sinistre?.numeroSinistreM || sinistre?.numeroSinistreP;

    const events = useMemo(() => {
        const list = [];
        const add = (event) => {
            if (event?.date) list.push(event);
        };

        // Survenance du sinistre (toujours en tête)
        add({
            key: "survenance",
            category: "declaration",
            order: 0,
            date: toDate(sinistre?.dateSinistre),
            dot: "#9ca3af",
            icon: <WarningOutlined />,
            title: "Survenance du sinistre",
            description: sinistre?.lieuSinistre ? `Lieu : ${sinistre.lieuSinistre}` : null,
        });

        // Déclaration (ouverture du dossier — précède toujours les actions de gestion)
        add({
            key: "declaration",
            category: "declaration",
            order: 1,
            date: toDate(sinistre?.dateDeclaration) || toDate(sinistre?.createdAt),
            dot: "#242166",
            icon: <FileAddOutlined />,
            title: "Déclaration du sinistre",
            description: numero ? `Dossier N° ${numero}` : null,
            author: sinistre?.createdBy,
        });

        // Évaluations
        (evaluations || []).forEach((e, index) => {
            const cfg = ETAT_EVALUATION[e?.etatEvaluation] || { label: e?.etatEvaluation, color: "default", dot: "#6b7280" };
            const provision = e?.provisionGaranties?.reduce((acc, p) => acc + (parseFloat(p?.provision) || 0), 0);
            add({
                key: `eval-${e?.codeId || index}`,
                category: "evaluation",
                order: 2,
                date: toDate(e?.createdAt),
                dot: cfg.dot,
                icon: <AuditOutlined />,
                title: `Évaluation${e?.recours ? " (recours)" : ""}`,
                tag: { text: cfg.label, color: cfg.color },
                description: provision ? `Provision : ${montantFcfa(provision)}` : (e?.motifRevision || null),
                extra: e?.etatEvaluation === "REJETE" ? e?.motifRejet : e?.motifRevisionDetaille,
                author: e?.reviseur?.username || e?.reviseur?.nomComplet || e?.createdBy,
            });
        });

        // Règlements / décaissements (inclut les rejetés → historique)
        (decaissements || []).forEach((r, index) => {
            const cfg = ETAT_DECAISSEMENT[r?.etatDecaissement] || { label: r?.etatDecaissement, color: "default", dot: "#6b7280" };
            add({
                key: `reg-${r?.codeId || index}`,
                category: "reglement",
                order: 2,
                date: toDate(r?.createdAt) || toDate(r?.dateReglement),
                dot: cfg.dot,
                icon: <DollarOutlined />,
                title: `Règlement — ${beneficiaireName(r)}`,
                tag: { text: cfg.label, color: cfg.color },
                description: [
                    r?.numero ? `Reg. N° ${r.numero}` : null,
                    montantFcfa(r?.montantGlobal),
                ].filter(Boolean).join(" · "),
                extra: r?.numeroCheque ? `Chèque N° ${r.numeroCheque}${r?.banque ? ` — ${r.banque}` : ""}` : null,
                author: r?.createdBy,
            });
            // Annulation TECHNIQUE (HSI015) : événement dédié « qui / quand / motif » sur le règlement
            // d'origine (montant positif) — on ignore la ligne négative de contre-passation pour ne pas doublonner.
            if (r?.motifAnnulationTechnique && Number(r?.montantGlobal || 0) >= 0) {
                add({
                    key: `reg-annul-${r?.codeId || index}`,
                    category: "reglement",
                    order: 2,
                    date: toDate(r?.updatedAt) || toDate(r?.createdAt),
                    dot: "#dc2626",
                    icon: <CloseCircleOutlined />,
                    title: `Règlement annulé techniquement — ${beneficiaireName(r)}`,
                    tag: { text: "Annulation technique", color: "error" },
                    description: [
                        r?.numero ? `Reg. N° ${r.numero}` : null,
                        `Motif : ${r.motifAnnulationTechnique}`,
                    ].filter(Boolean).join(" · "),
                    author: r?.updatedBy || r?.createdBy,
                });
            }
        });

        // Experts désignés
        (experts || []).forEach((x, index) => {
            add({
                key: `expert-${x?.codeId || index}`,
                category: "expert",
                order: 2,
                date: toDate(x?.createdAt),
                dot: "#0891b2",
                icon: <TeamOutlined />,
                title: `Expert désigné — ${`${x?.nom || ""} ${x?.prenom || ""}`.trim() || "Expert"}`,
                description: x?.specialite || x?.cabinet || null,
            });
        });

        // Rapports d'expertise
        (expertises || []).forEach((x, index) => {
            add({
                key: `expertise-${x?.codeId || index}`,
                category: "expertise",
                order: 2,
                date: toDate(x?.dateExpertise) || toDate(x?.createdAt),
                dot: "#7c3aed",
                icon: <FileSearchOutlined />,
                title: "Rapport d'expertise",
                description: montantFcfa(x?.montantExpertise || x?.coutReparation),
            });
        });

        // Statut courant du dossier (marqueur final si autre que « à valider »)
        const statut = STATUT_DOSSIER[sinistre?.sortDossier];
        if (statut && sinistre?.sortDossier !== "A") {
            add({
                key: "statut",
                category: "statut",
                order: 2,
                date: toDate(sinistre?.updatedAt),
                dot: statut.dot,
                icon: <FlagOutlined />,
                title: statut.label,
                author: sinistre?.updatedBy,
            });
        }

        // Tri par phase (survenance → déclaration → gestion) puis par date réelle,
        // afin que la déclaration précède toujours les évaluations/règlements même
        // lorsque les dates proviennent de sources différentes.
        return list.sort((a, b) => (a.order - b.order) || (a.date - b.date));
    }, [sinistre, evaluations, decaissements, experts, expertises, numero]);

    const filtered = useMemo(
        () => (filter === "all" ? events : events.filter((e) => e.category === filter)),
        [events, filter]
    );

    const timelineItems = filtered.map((e) => ({
        key: e.key,
        color: e.dot,
        dot: (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#fff",
                    border: `2px solid ${e.dot}`,
                    color: e.dot,
                    fontSize: 13,
                }}
            >
                {e.icon}
            </span>
        ),
        children: (
            <div style={{ paddingBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Text strong style={{ color: "#111827" }}>{e.title}</Text>
                    {e.tag && (
                        <Tag color={e.tag.color} style={{ margin: 0, borderRadius: 6, fontSize: 11 }}>
                            {e.tag.text}
                        </Tag>
                    )}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    {fmtDateTime(e.date)}
                    {e.author && <span> · par <Text type="secondary">{e.author}</Text></span>}
                </div>
                {e.description && (
                    <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>{e.description}</div>
                )}
                {e.extra && (
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{e.extra}</div>
                )}
            </div>
        ),
    }));

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#1a1a1a" }}>Historique du dossier</h2>
                    <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: 14 }}>
                        {events.length} évènement(s) depuis la déclaration
                    </p>
                </div>
                <Segmented options={FILTERS} value={filter} onChange={setFilter} />
            </div>

            <Card style={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                {timelineItems.length ? (
                    <Timeline mode="left" items={timelineItems} style={{ paddingTop: 8 }} />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Aucun évènement pour ce filtre"
                        style={{ padding: "40px 0" }}
                    />
                )}
            </Card>
        </div>
    );
};

export default HistoriqueTab;
